import { useCallback, useRef } from "react";
import {
  streamSimulation,
  type SimulationAssistantMessageEvent,
  type SimulationCompleteEvent,
  type SimulationInitialMessagesEvent,
  type SimulationRunRequestPayload,
  type SimulationUserMessageEvent,
  type SimulationUserTypingEvent,
} from "@financial-advisory/data-access/financial-advisory-api-service";

export interface SimulationCallbacks {
  onInitialMessages: (event: SimulationInitialMessagesEvent) => void;
  onUserMessage: (event: SimulationUserMessageEvent) => void;
  onUserTyping: (event: SimulationUserTypingEvent) => void;
  onAssistantMessage: (event: SimulationAssistantMessageEvent) => void;
  onSimulationComplete: (event: SimulationCompleteEvent) => void;
  onError: (error: Error) => void;
  onStreamEnd: () => void;
}

export function useSimulation(callbacks: SimulationCallbacks) {
  const abortControllerRef = useRef<AbortController | null>(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const startSimulation = useCallback(
    (payload: SimulationRunRequestPayload) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      streamSimulation(
        payload,
        {
          onInitialMessages: (e) => callbacksRef.current.onInitialMessages(e),
          onUserMessage: (e) => callbacksRef.current.onUserMessage(e),
          onUserTyping: (e) => callbacksRef.current.onUserTyping(e),
          onAssistantMessage: (e) =>
            callbacksRef.current.onAssistantMessage(e),
          onSimulationComplete: (e) =>
            callbacksRef.current.onSimulationComplete(e),
          onError: (e) => callbacksRef.current.onError(e),
          onStreamEnd: () => callbacksRef.current.onStreamEnd(),
        },
        controller.signal,
      );
    },
    [],
  );

  const stopSimulation = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  return { startSimulation, stopSimulation };
}
